import React, { forwardRef, useContext, useMemo } from 'react';
import { childrenToFlatString, parseColor } from '../utils.js';
import { MaterialContext, ThemeContext, } from './component-types.js';
const tempColor = new Float32Array(4);
/**
 * A 3D text component that renders text in 3D space with customizable styling.
 * Supports color customization and inherits text properties from theme context.
 *
 * @note
 * To use outline, ensure the Outline option is checked in the Wonderland Editor for the font.
 *
 * @component
 * @example
 * ```tsx
 * <Text
 *   color="#ff0000"
 *   fontSize={24}
 *   textEffect="shadow"
 *   textEffectColor="#440000"
 *   textEffectOffset={[0.05, -0.05]}
 * >
 *   Hello World
 * </Text>
 * ```
 *
 * @param {TextProps & {color?: Color}} props - The text properties
 * @param {Color} [props.color] - Text color. Falls back to theme color or material color
 * @param {Material} [props.material] - Custom material for the text. If not provided, uses cloned text material from context
 * @param {string} [props.text] - Alternative way to provide text content (prioritized over children)
 * @param {React.ReactNode} props.children - Text content to display (converted to string)
 * @param {React.Ref<Object3D>} ref - Forward ref to access the underlying 3D object
 * @returns {React.ReactElement} A 3D text element
 */
export const Text = forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e, _f;
    const context = useContext(MaterialContext);
    const theme = useContext(ThemeContext);
    const mat = (_a = props.material) !== null && _a !== void 0 ? _a : useMemo(() => { var _a; return (_a = context.textMaterial) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    if (mat) {
        mat.setColor(parseColor((_d = (_b = props.color) !== null && _b !== void 0 ? _b : (_c = theme.colors) === null || _c === void 0 ? void 0 : _c.text) !== null && _d !== void 0 ? _d : ((_e = props.material) !== null && _e !== void 0 ? _e : context.textMaterial)
            .color, tempColor));
        if (props.textEffectColor) {
            mat.setEffectColor(parseColor(props.textEffectColor, tempColor));
        }
    }
    return React.createElement('text3d', Object.assign(Object.assign({}, props), { material: mat, text: (_f = props.text) !== null && _f !== void 0 ? _f : childrenToFlatString(props.children), ref: ref }));
});
Text.displayName = 'Text';
